import { Organization } from "@common/types/organization";
import { OrganizationMemberClientAccessSource } from "@common/types/organizationMemberClientAccess";
import { noop } from "@common/helpers/function";
import { safeSessionStorage } from "@helpers/storage";
import { createContext, useContext, useMemo } from "react";
import { z } from "zod";

import { SubScopeContext } from "./subScopeContext.lib";

interface ScopeChangeParams {
    organizationId?: string | null;
    workspaceId?: string | null;
}

export interface InitializeScopeParams extends ScopeChangeParams {
    source?: OrganizationMemberClientAccessSource;
}

interface ScopeContextValue {
    organizationId?: string | null;
    workspaceId?: string | null;
    activeWorkspaceKey: string;
    activeOrganizationKey: string;
    changeActiveOrganization: (organization: Organization | null) => void;
    changeActiveWorkspace: (
        workspaceId: string | null,
        source?: OrganizationMemberClientAccessSource,
    ) => void;
    changeScope: (params: ScopeChangeParams) => void;
    initializeScope: (params: InitializeScopeParams) => void;
    clear: () => void;
}

export const ScopeContext = createContext<ScopeContextValue>({
    organizationId: undefined,
    workspaceId: undefined,
    activeWorkspaceKey: "anonymous",
    activeOrganizationKey: "anonymous",
    changeActiveOrganization: noop,
    changeActiveWorkspace: noop,
    changeScope: noop,
    initializeScope: noop,
    clear: noop,
});

export const scopeProxy: Pick<ScopeContextValue, "clear"> = {
    clear: noop,
};

const ACTIVE_WORKSPACE_KEY = "active-workspace-key";
const ACTIVE_ORGANIZATION_KEY = "active-organization-key";
const CURRENT_WORKSPACE_TAB_KEY = "current-workspace-tab";
const SCOPE_TAB_ID_KEY = "scope-tab-id";

export const getActiveWorkspaceKeyForUserId = (userId: string | number) =>
    [userId, ACTIVE_WORKSPACE_KEY].join("-");

export const getActiveOrganizationKeyForUserId = (userId: string | number) =>
    [userId, ACTIVE_ORGANIZATION_KEY].join("-");

export const getCurrentWorkspaceTabKeyForUserId = (userId: string | number) =>
    [userId, CURRENT_WORKSPACE_TAB_KEY].join("-");

const WorkspaceTabCellSchema = z.object({
    workspaceId: z.string(),
    tabId: z.string(),
    at: z.string(),
});

export type WorkspaceTabCell = z.infer<typeof WorkspaceTabCellSchema>;

export function parseWorkspaceTabCell(value: unknown): WorkspaceTabCell | null {
    const result = WorkspaceTabCellSchema.safeParse(value);
    return result.success ? result.data : null;
}

export function getTabId(): string {
    const existing = safeSessionStorage.getItem(SCOPE_TAB_ID_KEY);
    if (existing) {
        return existing;
    }
    const id = crypto.randomUUID();
    safeSessionStorage.setItem(SCOPE_TAB_ID_KEY, id);
    return id;
}

export function useScope() {
    const context = useContext(ScopeContext);
    if (!context) {
        throw new Error("useScope must be used within a ScopeProvider");
    }

    const subScope = useContext(SubScopeContext);

    return useMemo(() => {
        if (!subScope) {
            return context;
        }

        // SubScope wins for identity fields (an explicit null clears the value);
        // mutation methods always come from main scope.
        const workspaceId =
            subScope.workspaceId !== undefined
                ? subScope.workspaceId
                : context.workspaceId;

        return {
            ...context,
            workspaceId,
            activeWorkspaceKey: workspaceId ?? "anonymous",
            activeOrganizationKey: context.organizationId ?? "anonymous",
        };
    }, [context, subScope]);
}
